import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import InviteFriendsModal from 'components/organisms/invite-friends-modal';
import useGlobalState from 'utils/global-state';
import useWindowSize from 'utils/hooks/window-size';
import { getUserInitials, getUserQuadraColor } from 'utils/helpers';
import BrandSignature from 'components/atoms/brand-signature';
import { ReactComponent as PlusIcon } from 'assets/icons/invite_connection.svg';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import './header.scss';
import { NotAvailableModal } from '../../pages/portrait/not-available-modal';
import { getConnectionArrowVisibility } from './scroll-state';

const Header = ({
  renderExtension = false,
  openInviteModal = false,
  openNotAvailableModal = false,
  connections = [],
  onConnectionClick,
  backgroundColor = '#ffffff',
}) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const isPortraitPage = /^\/portrait(?:\/|$)/.test(pathname);
  const { isMobile } = useWindowSize();
  const connectionsRef = useRef(null);
  const [{ userData }] = useGlobalState();
  const [connectionActive, setConnectionActive] = useState(null);
  const [isModalOpened, setIsModalOpened] = useState(openInviteModal);
  const [isNotAvailableModalOpened, setNotAvailableModalOpened] = useState(false);
  const [isLeftArrowHidden, setIsLeftArrowHidden] = useState(true);
  const [isRightArrowHidden, setIsRightArrowHidden] = useState(true);

  useEffect(() => {
    if (!renderExtension || !connectionsRef.current) return undefined;

    const element = connectionsRef.current;
    const updateArrowVisibility = () => {
      const visibility = getConnectionArrowVisibility(element);

      setIsLeftArrowHidden(visibility.hideLeft);
      setIsRightArrowHidden(visibility.hideRight);
    };
    const animationFrame = window.requestAnimationFrame(updateArrowVisibility);
    const resizeObserver = window.ResizeObserver
      ? new window.ResizeObserver(updateArrowVisibility)
      : null;

    element.style.scrollBehavior = 'smooth';
    if (resizeObserver) resizeObserver.observe(element);
    window.addEventListener('resize', updateArrowVisibility);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', updateArrowVisibility);
    };
  }, [renderExtension, connections.length, isMobile]);

  const handleConnectionsScroll = () => {
    if (connectionsRef.current) {
      const visibility = getConnectionArrowVisibility(connectionsRef.current);

      setIsLeftArrowHidden(visibility.hideLeft);
      setIsRightArrowHidden(visibility.hideRight);
    }
  };

  const renderConnection = (uuid, name, photo, quadra, index) => (
    <div className="connection" key={index}>
      {photo ? (
        <div
          className={`connection-photo-wrapper${
            index === connectionActive ? ' is-active' : ''
          }`}
          style={{
            border: index === connectionActive
              ? `2px solid ${getUserQuadraColor(quadra)}`
              : 0,
          }}
          onClick={openNotAvailableModal
            ? () => setNotAvailableModalOpened(true)
            : () => {
              onConnectionClick(uuid);
              setConnectionActive(index);
            }}
        >
          <div
            className="connection-photo"
            style={{ backgroundImage: `url(${photo})` }}
          />
        </div>
      ) : (
        <span className="connection-photo user-initials">
          {getUserInitials(name === 'Me' ? userData.name : name)}
        </span>
      )}
      <div
        className="connection-name"
        onClick={() => {
          onConnectionClick(uuid);
          setConnectionActive(index);
        }}
      >
        {name}
      </div>
    </div>
  );

  const renderHeaderExtension = () => (
    <div className="header-extension">
      <div className="connections">
        <div
          className="connections-items"
          ref={connectionsRef}
          onScroll={() => handleConnectionsScroll()}
        >
          {renderConnection(userData.uuid, 'Me', userData.avatar)}
          {connections.length > 0
            ? connections.map(
              ({
                uuid, name, avatar, quadra,
              }, index) => renderConnection(
                uuid,
                name,
                avatar,
                quadra,
                index,
              ),
            )
            : isMobile && (
            <div className="no-connections text-xs">
              You have no connections yet. Invite friends
            </div>
            )}
        </div>
        {!isMobile && (
        <div className="connections-arrows">
          <div
            className={`connection-arrow left-arrow${
              isLeftArrowHidden ? ' hidden' : ''
            }`}
            onClick={() => {
              connectionsRef.current.scrollLeft -= 60;
            }}
          >
            <div className="connection-arrow-icon">
              <Arrow />
            </div>
          </div>
          <div
            className={`connection-arrow right-arrow${
              isRightArrowHidden ? ' hidden' : ''
            }`}
            onClick={() => {
              connectionsRef.current.scrollLeft += 60;
            }}
          >
            <div className="gradient" />
            <div className="connection-arrow-icon">
              <Arrow />
            </div>
          </div>
        </div>
        )}
      </div>
      <div
        className="plus-icon-wrapper"
        onClick={() => setIsModalOpened(true)}
      >
        {isMobile && <div className="gradient" />}
        <div className="plus-icon">
          <PlusIcon />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <InviteFriendsModal
        isModalOpened={isModalOpened}
        closeModal={() => setIsModalOpened(false)}
      />
      <NotAvailableModal
        isNotAvailableModalOpened={isNotAvailableModalOpened}
        setNotAvailableModalOpened={setNotAvailableModalOpened}
      />
      <header>
        <div
          className="header-main"
          style={{ backgroundColor }}
        >
          <div className="header-left">
            <div
              className="header-logo"
              onClick={() => history.push('/portrait-cards')}
            >
              <BrandSignature compact={isMobile} />
            </div>
            {renderExtension
              && !isMobile
              && renderHeaderExtension()}
            {renderExtension
              && !isMobile
              && connections.length === 0 && (
                <div className="no-connections text-xs">
                  You have no connections yet. Invite friends
                </div>
            )}
          </div>
          <button
            type="button"
            className={`header-username${isPortraitPage ? ' header-username--compact' : ''}`}
            aria-label="Open profile"
            title="Open profile"
            onClick={() => history.push('/account')}
          >
            <span className="profile-control-avatar">
              {userData.avatar ? (
                <span
                  className="user-avatar"
                  style={{
                    backgroundImage: `url(${userData.avatar})`,
                  }}
                />
              ) : (
                <span className="user-initials">
                  {getUserInitials(userData.name)}
                </span>
              )}
            </span>
            {!isPortraitPage && (
              <span className="profile-control-copy">
                <span className="profile-control-label">Profile</span>
                <span className="profile-control-chevron" aria-hidden="true">›</span>
              </span>
            )}
          </button>
        </div>
        {renderExtension && isMobile && renderHeaderExtension()}
      </header>
    </>
  );
};

Header.propTypes = {
  renderExtension: PropTypes.bool,
  openInviteModal: PropTypes.bool,
  connections: PropTypes.arrayOf(PropTypes.shape({
    uuid: PropTypes.string,
    name: PropTypes.string,
    avatar: PropTypes.string,
    quadra: PropTypes.string,
  })),
  onConnectionClick: PropTypes.func,
  backgroundColor: PropTypes.string,
};

Header.defaultProps = {
  renderExtension: false,
  openInviteModal: false,
  connections: [],
  onConnectionClick: () => {},
  backgroundColor: '#ffffff',
};

export default Header;
